import React, { useCallback, useEffect, useRef } from 'react';

import { downloadSettingExcel } from "src/utils/blob/blobUtil";

import { defaultWMS1101_R01Filter, WMS1101_R01Filter, useWMS1101_R01Store } from 'src/containers/mes/WMS/WMS1101_R01/Store_WMS1101_R01';
import useFetchActionbarAuth from 'src/hooks/useFetchActionbarAuth';
import { Tab } from 'src/store/useUiuxStore';
import { useLoadingSpinnerStore } from 'src/store/util/loadingSpinnerUtil';
import { showError, ShowSuccessFetch, showWarning } from 'src/utils/toast/toastUtil';

import { downloadExcel_WMS1101_R01, getWMS1101_R01AllCount, getWMS1101_R01DetailList, getWMS1101_R01List } from "./Api_WMS1101_R01";
import { defaultWMS1101_R01DetailDto, defaultWMS1101_R01HeaderDto, WMS1101_R01DetailDto, WMS1101_R01HeaderDto } from './Dto_WMS1101_R01';
import { AgGridReact } from 'ag-grid-react';
import { gridRefSelectedRowHandler } from 'src/utils/grid/GridHandlerContent';

interface Props {
	gridRef: React.RefObject<AgGridReact<WMS1101_R01HeaderDto> | null>;
	detailGridRef: React.RefObject<AgGridReact<WMS1101_R01DetailDto> | null>;

	tabList: Tab[] | undefined;
	setMessage: (message: string) => void;

	containerRef: React.RefObject<HTMLDivElement | null>;
	isResizing: React.MutableRefObject<boolean>;
}

export const Content_WMS1101_R01 = (props: Props) => {
	const {
		gridRef, detailGridRef,
		setMessage,
	} = props;

	const {
		WMS1101_R01Filter,
		WMS1101_R01List,
		setWMS1101_R01List,
		setWMS1101_R01PastFilter,
		selectedWMS1101_R01,
		setSelectedWMS1101_R01,
		setSelectedWMS1101_R01DetailList,
		setWMS1101_R01DetailList,
		setOriginalWMS1101_R01DetailList,
		setAllCount,
		initData,
		setScrollTop, setDetailScrollTop,
	} = useWMS1101_R01Store();
	const { setLoadingSpinner } = useLoadingSpinnerStore();

	useFetchActionbarAuth("WMS1101_R01");

	const prevWmsOrderNoRef = useRef<string | undefined>(undefined);

	const handleExcelClick = useCallback(async () => {
		if (!selectedWMS1101_R01) {
			showWarning("선택된 입고정보내역이 없습니다.");
			return;
		}
		setLoadingSpinner(true);

		const response = await downloadExcel_WMS1101_R01('', selectedWMS1101_R01.transportNo, WMS1101_R01List.length ?? 0);

		downloadSettingExcel({ responseData: response.data.data, fileName: '입고정보 등록-excel.xlsx' });

		setLoadingSpinner(false);
	}, [selectedWMS1101_R01, WMS1101_R01List, setLoadingSpinner]);

	const getParams = (filter: WMS1101_R01Filter) => {
		const params: Partial<WMS1101_R01Filter> = {
			dateRange: filter.dateRange || undefined,
			customCd: filter.customCd || undefined,
		};
		return params;
	};

	const renewalAllCount = useCallback(async () => {
		const response = await getWMS1101_R01AllCount(getParams(WMS1101_R01Filter));
		setAllCount(response.data.data);
		return response.data.data;
	}, [WMS1101_R01Filter, setAllCount]);

	const fetchData = useCallback(async () => {
		setLoadingSpinner(true);
		setScrollTop(null);
		setDetailScrollTop(null);

		const responseData = (await getWMS1101_R01List(
			'',
			getParams(WMS1101_R01Filter),
			WMS1101_R01List.length ?? 0
		));

		let newList = [...responseData.data.data] as WMS1101_R01HeaderDto[];
		setWMS1101_R01List(newList);

		const processAllCount = await renewalAllCount();
		if (newList && newList.length > 0) {
			gridRefSelectedRowHandler(
				gridRef,
				setSelectedWMS1101_R01,
				() => true,
			);
			setWMS1101_R01PastFilter(WMS1101_R01Filter);
			ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
			setTimeout(() => setMessage(""), 5000);
		} else {
			showWarning('데이터가 없습니다.');
			setSelectedWMS1101_R01(defaultWMS1101_R01HeaderDto);
			setWMS1101_R01List([]);
			setWMS1101_R01DetailList([]);
			setSelectedWMS1101_R01DetailList([]);
			setOriginalWMS1101_R01DetailList([]);
		}
		setLoadingSpinner(false);
		return responseData;
	}, [WMS1101_R01Filter, WMS1101_R01List, setWMS1101_R01List, setWMS1101_R01PastFilter, setSelectedWMS1101_R01, setScrollTop, setDetailScrollTop, gridRef, setMessage, renewalAllCount, setLoadingSpinner, setWMS1101_R01DetailList, setSelectedWMS1101_R01DetailList, setOriginalWMS1101_R01DetailList]);

	const defaultWMS1101_R01DetailRow = useCallback(() => {
		return {
			...defaultWMS1101_R01DetailDto,
			inserted: true,
		};
	}, []);

	const initContainerData = useCallback(() => {
		initData();
	}, [initData]);

	const handleDeleteClick = useCallback(() => {
	}, [selectedWMS1101_R01]);

	const handleCreateClick = () => {
		setSelectedWMS1101_R01(undefined);
		setWMS1101_R01DetailList([{ ...defaultWMS1101_R01DetailRow(), inserted: true }]);
		setSelectedWMS1101_R01DetailList([]);
		setOriginalWMS1101_R01DetailList([]);
	};

	const handleSaveClick = useCallback(async () => {
		setTimeout(() => {
			setMessage('');
		}, 5000);
	}, [setMessage]);

	const handleFetchDetailList = useCallback(async (transportCode: string) => {
		try {
			const detailResponse = await getWMS1101_R01DetailList(transportCode);
			const detailData = detailResponse.data.data;

			if (detailData && detailData.length > 0) {
				const updatedDetails = detailData.map((value: WMS1101_R01DetailDto) => ({
					...value,
					valueIndex: value.valueIndex,
					orderIndex: value.orderIndex,
					orderIndexSeq: value.orderIndexSeq,
				}));
				setWMS1101_R01DetailList(updatedDetails);
				setOriginalWMS1101_R01DetailList(detailData);
				setMessage(`${selectedWMS1101_R01?.compNm}의 입고지시내역 ${detailData.length}건을 조회하였습니다.`);
				setTimeout(() => setMessage(""), 5000);
				setSelectedWMS1101_R01DetailList([String(updatedDetails[0]?.valueIndex ?? 1)]);
			} else {
				showError('상세내역이 없습니다.');
				setWMS1101_R01DetailList([]);
			}
		} catch (error) {
			console.error("상세 내역 조회 실패:", error);
			showError('상세 내역을 조회하는 중 오류가 발생했습니다.');
			setWMS1101_R01DetailList([]);
		}
	}, [
		setMessage,
		setWMS1101_R01DetailList,
		setOriginalWMS1101_R01DetailList,
		selectedWMS1101_R01?.compNm,
		setSelectedWMS1101_R01DetailList,
	]);

	useEffect(() => {
		const fetchAllDetailsForSelectedRow = async () => {
			if (!selectedWMS1101_R01 || !selectedWMS1101_R01.transportCode) {
				if (!selectedWMS1101_R01) {
					setWMS1101_R01DetailList([]);
					setOriginalWMS1101_R01DetailList([]);
				}
				prevWmsOrderNoRef.current = undefined;
				return;
			}

			handleFetchDetailList(selectedWMS1101_R01.transportCode);
			prevWmsOrderNoRef.current = selectedWMS1101_R01.transportNo;
		};

		fetchAllDetailsForSelectedRow();
	}, [selectedWMS1101_R01, handleFetchDetailList, setWMS1101_R01DetailList, setOriginalWMS1101_R01DetailList]);

	return {
		handleExcelClick,
		fetchData,
		defaultWMS1101_R01DetailRow,
		initContainerData,
		handleDeleteClick,
		handleCreateClick,
		handleSaveClick,
	};
}
